import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';

const s = {
  page: { minHeight:'100vh', background:'var(--paper)', display:'flex', alignItems:'center', justifyContent:'center', padding:24 },
  card: { background:'var(--card)', border:'1px solid var(--line-card)', borderRadius:0, width:'100%', maxWidth:440, overflow:'hidden' },
  header: { background:'var(--ink)', padding:'28px 32px' },
  brand: { fontFamily:'var(--font-display)', color:'var(--paper)', fontSize:20 },
  body: { padding:'36px 32px' },
  title: { fontFamily:'var(--font-display)', fontSize:20, fontWeight:700, marginBottom:8, color:'var(--ink)' },
  sub: { color:'var(--muted)', fontSize:13, lineHeight:1.6, marginBottom:32 },
  codeRow: { display:'flex', gap:10, justifyContent:'center', marginBottom:24 },
  codeInput: { width:52, height:60, textAlign:'center', fontSize:24, fontFamily:'var(--font-mono)', fontWeight:700, border:'2px solid var(--line)', borderRadius:0, outline:'none', transition:'border-color 0.15s', letterSpacing:0, background:'var(--card)' },
  btn: { width:'100%', background:'var(--ink)', color:'var(--paper)', border:'none', padding:'13px', fontSize:15, fontWeight:600, borderRadius:0, cursor:'pointer' },
  resend: { textAlign:'center', marginTop:16, fontSize:13, color:'var(--muted)' },
  resendBtn: { color:'var(--ink)', fontWeight:600, cursor:'pointer', background:'none', border:'none', fontSize:13 },
  error: { background:'var(--card)', border:'1px solid var(--danger)', color:'var(--danger)', padding:'10px 14px', borderRadius:0, fontSize:13, marginBottom:16 },
  success: { background:'#f0fdf4', color:'#15803d', padding:'10px 14px', borderRadius:0, fontSize:13, marginBottom:16 }
};

export default function VerifyEmail() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const userId = location.state?.userId;
  const email = location.state?.email;
  const [digits, setDigits] = useState(['', '', '', '', '', '']);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const inputRefs = useRef([]);

  useEffect(() => {
    if (!userId) navigate('/register');
  }, [userId]);

  useEffect(() => {
    if (resendCooldown > 0) {
      const t = setTimeout(() => setResendCooldown(c => c - 1), 1000);
      return () => clearTimeout(t);
    }
  }, [resendCooldown]);

  const handleDigitChange = (idx, val) => {
    if (!/^\d*$/.test(val)) return;
    const newDigits = [...digits];
    newDigits[idx] = val.slice(-1);
    setDigits(newDigits);
    if (val && idx < 5) inputRefs.current[idx + 1]?.focus();
    if (newDigits.every(d => d)) handleVerify(newDigits.join(''));
  };

  const handleKeyDown = (idx, e) => {
    if (e.key === 'Backspace' && !digits[idx] && idx > 0) inputRefs.current[idx - 1]?.focus();
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const paste = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    const newDigits = [...digits];
    paste.split('').forEach((d, i) => { if (i < 6) newDigits[i] = d; });
    setDigits(newDigits);
    if (paste.length === 6) handleVerify(paste);
  };

  const handleVerify = async (code) => {
    setError('');
    setLoading(true);
    try {
      const { data } = await axios.post('/api/auth/verify-email', { userId, code });
      login(data.token, data.user);
      navigate('/');
    } catch (err) {
      setError(err.response?.data?.message || 'Verification failed');
      setDigits(['', '', '', '', '', '']);
      inputRefs.current[0]?.focus();
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    try {
      await axios.post('/api/auth/resend-code', { userId });
      setSuccess('Code resent! Check your email.');
      setResendCooldown(60);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to resend');
    }
  };

  return (
    <div style={s.page}>
      <div style={s.card} className="fade-in">
        <div style={s.header}><div style={s.brand}>BioMedArticles</div></div>
        <div style={s.body}>
          <h2 style={s.title}>Verify your email</h2>
          <p style={s.sub}>We sent a 6-digit code to <strong>{email || 'your email'}</strong>. Enter it below to activate your account.</p>
          {error && <div style={s.error}>{error}</div>}
          {success && <div style={s.success}>{success}</div>}
          <div style={s.codeRow} onPaste={handlePaste}>
            {digits.map((d, i) => (
              <input
                key={i}
                ref={el => inputRefs.current[i] = el}
                style={{ ...s.codeInput, borderColor: d ? 'var(--ink)' : 'var(--line)' }}
                type="text"
                inputMode="numeric"
                maxLength={1}
                value={d}
                onChange={e => handleDigitChange(i, e.target.value)}
                onKeyDown={e => handleKeyDown(i, e)}
                autoFocus={i === 0}
              />
            ))}
          </div>
          <button style={{ ...s.btn, opacity: loading ? 0.7 : 1 }} onClick={() => handleVerify(digits.join(''))} disabled={loading || digits.join('').length < 6}>
            {loading ? 'Verifying...' : 'Verify Email →'}
          </button>
          <div style={s.resend}>
            Didn't receive it?{' '}
            {resendCooldown > 0 ? (
              <span style={{ color:'var(--muted)' }}>Resend in {resendCooldown}s</span>
            ) : (
              <button style={s.resendBtn} onClick={handleResend}>Resend code</button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
